import { Box, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import cx from 'classnames';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import MConfirmation from "../Modals/MConfirmation";
import MConfirmDelete from "../Modals/MConfirmDelete";
import DropdownButton from "../../theme/buttons/DropdownButton";
import Shbutton from "../../theme/buttons/Shbutton";
import ShRhDataGridSelectedActions from "../../theme/rhdatagrid/ShRhDataGridSelectedActions";
import SidepanelLoader from "../../theme/sidepanel2/SidepanelLoader";
import Subheading from "../../theme/text/Subheading";
import { useTopbar } from "../../../contexts/TopbarProvider";
import { useDeleteBiReportByIdMutation, useEditBiReportByIdMutation, useGetBiDetails2Query, useGetBiDetailsByIdQuery } from "../../../newapi/global/businessIntelligence";
import { datagridPageSizeOptions } from "../../../newapi/global/filter/DataGridGeneralOptions";
import { tokens } from "../../../theme";
import { showToastMessageDeleteRequest, showToastMessageRequestError, showToastMessageUpdateRequest } from "../../../utils/toasts";
import { RhChart } from "./components/RhChart";
import { RhDataTable } from "./components/RhDataTable";
import { useReportHubFilters } from "./ReportHubFilterContext";
import { getPivotView, purgeNonSerializable } from "./reportUtils";
import { reporthubChartOptionKeys, reportTypeOptionKeys } from "./chartOptions";
import { getReportForEnvironment, getReportKey, getReportLabelKey, reportTypes } from "./reports";
import { toast } from "react-toastify";
import { RhPivotTable } from "./components/RhPivotTable";
import { RhPivotHeader } from "./components/RhPivotHeader";
import { RhPivotExportButton } from "./components/pivottable/RhPivotExportButton";
// import { useGetRelationDashboardProductsQuery } from "../../../newapi/warehouse/productSlice";
const SpStockDetails = SidepanelLoader(() => import("../Sidepanels/SpStockDetails/SPStockDetails"));
// global/Sidepanels/SpStockDetails/SPStockDetails


const ReportDetailPageViewer = () => {
    
    const { t, ready,i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const dispatch = useDispatch();
    const [currentReport, setCurrentReport] = useState(null);
    const [chartInfo,setChartInfo] = useState(null);
    const [data,setData]=useState(null);
    const heatmapMode = getPivotView(currentReport).heatmap;
    const filtersRegistry = useReportHubFilters();

    const { showExtraComponent, clearExtraComponent,showExtraTitle,clearExtraTitle } = useTopbar();

    const [saveReport, {isLoading:reportSaving}] = useEditBiReportByIdMutation();
    const [deleteReport, {isLoading:reportDeleting}] = useDeleteBiReportByIdMutation();

    const { id } = useParams();
    const [initialLoaded, seIinitialLoaded] = useState(false);
    const {data:initialData, isSuccess:initialSuccess, isLoading:initialLoading,isFetching:initialFetching,isError:initialIsError, refetch:initialRefetch} = useGetBiDetailsByIdQuery(id,{
        skip: !id || initialLoaded,
    })
    const [page,setPage] = useState(1);
    const [pageSize,setPageSize] = useState(datagridPageSizeOptions[0]);
    const [sortModel, setSortModel] = useState(null);

    const handleSortChange = (newSortModel) => {
        setSortModel(newSortModel);
        setPage(1);
    };

    useEffect(() => {
        if(!initialLoading && !initialFetching && initialData) {
            setChartInfo(initialData.chart);
            setData(initialData.data);
            filtersRegistry.setCurrentReportKey(initialData.savedReport.config.type);
            filtersRegistry.setChartSettings(getReportForEnvironment(getReportKey(initialData.savedReport.config), filtersRegistry.environment));
            setCurrentReport(initialData.savedReport.config);
            seIinitialLoaded(true);
        }
    },[initialData, initialLoading, initialFetching]);

    // An `all` category names no values on purpose — the backend resolves them per environment — so an
    // empty list is only a half-built report when the flag is off.
    const categoryInvalid =
    currentReport?.category?.use === true &&
    currentReport?.category?.all !== true &&
    (!currentReport?.category?.values || currentReport.category.values.length === 0);

    const filterInvalid = Object.entries(currentReport?.filter || {}).filter(
        ([key, f]) => Array.isArray(f.value) && f.value.length === 0
    );

    // track previous display type
    const prevDisplayTypeRef = useRef(currentReport?.displayType);

    // update the ref for next render
    useEffect(() => {
        prevDisplayTypeRef.current = currentReport?.displayType;
    }, [currentReport?.displayType]);

    // Skip data fetch for invalid states only
    // Name and displayType changes should NOT skip the query - they're purely metadata/visual
    const shouldSkip = !currentReport || categoryInvalid || filterInvalid.length > 0;

    // Create a query body that excludes displayType and name (purely visual/metadata, doesn't affect data)
    const queryBody = useMemo(() => {
        if (!currentReport) return null;
        const { displayType, name, ...reportWithoutMetadata } = currentReport;
        return purgeNonSerializable(reportWithoutMetadata);
    }, [currentReport?.type, currentReport?.table, currentReport?.date, currentReport?.category, currentReport?.filter, currentReport?.subtype]);

    // Track previous queryBody to detect filter changes vs pagination changes
    const prevQueryBodyRef = useRef(queryBody);
    const [chartFilterChanging, setChartFilterChanging] = useState(false);

    // Detect when filters change (not pagination). Don't flag "changing" when the query is skipped
    // (e.g. category mode with no category chosen yet) — there's no fetch to wait on, so it would
    // otherwise stay true forever and leave the chart stuck in its loading/blur state.
    useEffect(() => {
        const queryBodyChanged = JSON.stringify(prevQueryBodyRef.current) !== JSON.stringify(queryBody);
        if (queryBodyChanged && queryBody !== null && !shouldSkip) {
            setChartFilterChanging(true);
        }
        prevQueryBodyRef.current = queryBody;
    }, [queryBody, shouldSkip]);

    const queryArgs = useMemo(() => ({
        args: {
            page,
            size: pageSize,
            body: queryBody,
            sortBy: sortModel?.field || null,
            sortDirection: sortModel?.type || null,
        },
    }), [page, pageSize, queryBody, sortModel?.field, sortModel?.type]);

    const { data: biData, isSuccess, isLoading, isFetching: fetchFetching, isError, refetch, isUninitialized } = useGetBiDetails2Query(
        queryArgs,
        {
            skip: shouldSkip,
        }
    );

    // RTK's refetch throws "Cannot refetch a query that has not been started yet" when the query was
    // skipped (e.g. category mode with no selection). Guard the retry so the error button is inert then.
    const handleRetry = useCallback(() => {
        if (isUninitialized) return;
        refetch();
    }, [isUninitialized, refetch]);

    // Reset chartFilterChanging when fetch completes, or when the query is skipped (no fetch will run).
    useEffect(() => {
        if (shouldSkip || (!fetchFetching && !isLoading)) {
            setChartFilterChanging(false);
        }
    }, [fetchFetching, isLoading, shouldSkip]);

    useEffect(() => {
        if(!fetchFetching && !isLoading && biData) {
            if(biData.chart) {
                setChartInfo(biData.chart);
            }
            setData(biData.data);
        }
    },[biData,fetchFetching,isLoading])

    useEffect(() => {
        if(currentReport !== null && prevDisplayTypeRef?.current) {
            if(prevDisplayTypeRef.current !== currentReport?.displayType) {
                setChartInfo(prev => ({
                    ...prev,
                    displayType:currentReport?.displayType
                }))
            }
        }
    },[currentReport, prevDisplayTypeRef])

    useEffect(() => {
        showExtraTitle(
            <Box
                sx={{
                    display:'flex',
                    alignItems:"center",
                    gap:1,
                    color:colors.txt["secondary"]
                }}
            >
                {t("reporthub")}&nbsp;
                /
                <Subheading>
                    {t(getReportLabelKey(getReportKey(currentReport)))}
                </Subheading>
            </Box>
        )

        return () => {
            clearExtraTitle();
        };
    }, [t,i18n.language,theme.palette.mode,currentReport?.type,currentReport?.reportKey])

    const handleDeleteReport =() => {
        if(reportDeleting) return;
        deleteReport(id).unwrap().then(res => {
            showToastMessageDeleteRequest(t,res);
        }).catch(err => {
            console.error(err);
            showToastMessageRequestError(t("create_failed"),t,err);
        })
    }

    const [nameError,setNameError] = useState(false);

    const handleEditReport =() => {
        if(reportSaving) return;
        if(currentReport.name.length === 0) {
            setNameError(true);
            toast.error(t("name_not_filled_in"), {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
            return;
        }
        let reportToSave = {...currentReport};

        let categorySettings = filtersRegistry.chartSettings?.categorySettings?.();
        if(categorySettings?.defaultDateTime) {
            reportToSave.date = {
                type: categorySettings.defaultDateTime.type,
                frame: categorySettings.defaultDateTime.frame,
                ...(categorySettings.defaultDateTime.days && {days: categorySettings.defaultDateTime.days}),
                ...(categorySettings.defaultDateTime.to && {to: categorySettings.defaultDateTime.to}),
                ...(categorySettings.defaultDateTime.from && {from: categorySettings.defaultDateTime.from}),
            };
        }
        saveReport({id:id,name:reportToSave?.name,config:reportToSave}).unwrap().then(res => {
            showToastMessageUpdateRequest(t,res);
        }).catch(err => {
            console.error(err);
            showToastMessageRequestError(t("create_failed"),t,err);
        })
    }

    const [open, setOpen] = useState(false);

    const confirmDeleteModal = {
        open: open,
        setOpen: setOpen,
        confirm: handleDeleteReport,
    };

    const handleDeleteClick = async () => {
        setOpen(true);
    };

    const [editOpen, setEditOpen] = useState(false);

    const confirmEditModal = {
        open: editOpen,
        setOpen: setEditOpen,
        confirm: handleEditReport,
    };

    const handleEditClick = async () => {
        setEditOpen(true);
    };
        const collapsed = useSelector((state) => state.settings.environment.collapsed);

    const chartOption = getReportForEnvironment(getReportKey(currentReport), filtersRegistry.environment);
    return (
        <Box 
            sx={{
                background:colors.bg['tertiary'], 
                width:1,
                paddingTop:9,
            }}
        >
            {typeof currentReport?.reportType === "undefined" || currentReport?.reportType === reportTypeOptionKeys.WITHCHART ? (
                <>

                    <RhChart
                        isError={isError || initialIsError}
                        isLoading={isLoading || initialLoading}
                        fetchFetching={chartFilterChanging || initialFetching}
                        chartInfo={chartInfo}
                        currentReport={currentReport}
                        setCurrentReport={setCurrentReport}
                        defaultNameEdit={false}
                        refetch={handleRetry}
                        nameError={nameError}
                        setNameError={setNameError}
                    />

                    <RhDataTable
                        chartInfo={chartInfo}
                        currentReport={currentReport}
                        data={data}
                        setPageSize={setPageSize}
                        isSuccess={isSuccess || initialSuccess}
                        isError={isError || initialIsError}
                        setPage={setPage}
                        isLoading={isLoading || fetchFetching || initialLoading || initialFetching}
                        onSortChange={handleSortChange}
                        selectedActions={
                            <Box
                                sx={{
                                    height:1,
                                }}
                            >
                                <Box
                                    sx={{
                                        height:1,
                                        background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[100],
                                        px:1,
                                        borderTopRightRadius:28,
                                        borderBottomRightRadius:28,
                                        display:'flex',
                                        alignItems:'center',
                                        justifyContent:'center',
                                        gap:1,
                                        "@media screen and (max-width: 47.9375em)" : {
                                            display:'none'
                                        }
                                    }}
                                >
                                    <Shbutton
                                        isLoading={reportDeleting}
                                        onClick={handleDeleteClick}
                                        variant="contained"
                                        color={"red"}
                                        sx={{
                                            whiteSpace:"nowrap",
                                            px:3,
                                            minWidth:"unset",
                                        }}
                                    >
                                        {t("delete_report")}
                                    </Shbutton>
                                    <Shbutton
                                        isLoading={reportDeleting}
                                        onClick={handleEditClick}
                                        variant="contained"
                                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                        sx={{
                                            whiteSpace:"nowrap",
                                            px:3,
                                            minWidth:"unset",
                                        }}
                                    >
                                        {t("save_edit_report")}
                                    </Shbutton>
                                </Box>
                                <ShRhDataGridSelectedActions
                                    sx={{
                                        height:48,
                                        borderRadius:7,
                                        display:"none",
                                        px:0.5,
                                        alignItems:'center',
                                        background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                        "@media screen and (max-width: 47.9375em)" : {
                                            display:'flex'
                                        }

                                    }}
                                >

                                    <DropdownButton
                                        actionIconColor={theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]}
                                        onClick={handleEditClick} 
                                        icon={<EditIcon/>} 
                                        text={t("save_edit_report")}
                                    />
                                    <DropdownButton
                                        actionIconColor={colors.red[400]}
                                        onClick={handleDeleteClick} 
                                        icon={<DeleteOutlinedIcon/>} 
                                        text={t("delete_report")}
                                    />
                                </ShRhDataGridSelectedActions>
                            </Box>
                        }
                    />
                </>
            )
            : currentReport?.reportType === reportTypeOptionKeys.PIVOTTABLE ?(
                <>
                    <RhPivotHeader
                        isError={isError}
                        isLoading={isLoading || initialLoading}
                        fetchFetching={chartFilterChanging || initialFetching}
                        chartInfo={chartInfo}
                        currentReport={currentReport}
                        setCurrentReport={setCurrentReport}
                        defaultNameEdit
                        refetch={handleRetry}
                        nameError={nameError}
                        setNameError={setNameError}
                        heatmapMode={heatmapMode}
                    />
                    <RhPivotTable
                        data={biData?.pivot}
                        chartOption={chartOption}
                        currentReport={currentReport}
                        setCurrentReport={setCurrentReport}
                        isError={isError}
                        isLoading={isLoading || initialLoading || fetchFetching || initialFetching}
                        heatmapMode={heatmapMode}
                        actions={
                            <Box
                                className={cx(undefined,{
                                    'collapsed':collapsed,
                                })}
                                sx={{
                                    position:"fixed",
                                    bottom:16,
                                    zIndex:theme.zIndex.appBar, // below modal z-index so an open filter popover closes (and commits) before save is clicked
                                    left: "calc(50% + 342px)",
                                    transform:"translateX(calc(-50% - 171px))",
                                    background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[100],
                                    px:1,
                                    borderRadius:7,
                                    display:'flex',
                                    alignItems:'center',
                                    justifyContent:'center',
                                    gap:1,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        display:'none'
                                    },
                                    "&.collapsed": {
                                        left:"50%" ,
                                        transform: "translateX(-50%)" ,
                                        transition:"500ms",

                                    },
                                }}
                            >
                                <Shbutton
                                    isLoading={reportDeleting}
                                    onClick={handleDeleteClick}
                                    variant="contained"
                                    color={"red"}
                                    sx={{
                                        whiteSpace:"nowrap",
                                        px:3,
                                        minWidth:"unset",
                                    }}
                                >
                                    {t("delete_report")}
                                </Shbutton>
                                <Shbutton
                                    isLoading={reportDeleting}
                                    onClick={handleEditClick}
                                    variant="contained"
                                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                    sx={{
                                        whiteSpace:"nowrap",
                                        px:3,
                                        minWidth:"unset",
                                    }}
                                >
                                    {t("save_edit_report")}
                                </Shbutton>
                                <RhPivotExportButton
                                    data={biData?.pivot}
                                    currentReport={currentReport}
                                    chartOption={chartOption}
                                />
                            </Box>
                        }
                    />
                </>
            ): (
                <Box/>
            )}
            
            <MConfirmDelete
                open={confirmDeleteModal.open}
                handleClose={() => confirmDeleteModal.setOpen(false)}
                // data={props.streamRule}
                confirm={confirmDeleteModal.confirm}
            />
            <MConfirmation
                open={confirmEditModal.open}
                handleClose={() => confirmEditModal.setOpen(false)}
                // data={props.streamRule}
                confirm={confirmEditModal.confirm}
                title={t("save_edit_report")}
            />

        
        </Box>
    );
};

export default ReportDetailPageViewer;
